import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Typography,
  Select,
  MenuItem,
  TextField,
  IconButton,
  DialogContent,
  Box,
  Divider,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import OrderServices from '../../services/orders';
import QuantitySelector from '../../components/QuantitySelector/QuantitySelector';

export default function UnexpectedItem({ isOpen, handleClose, submitChanges }) {
  const [category, setCategory] = useState('');
  const [skus, setSkus] = useState([
    { sku: '', quantity: 0, vendor: '', description: '', color: '', theme: '' },
  ]);
  const [order, setOrder] = useState('');
  const [isOrderNumberChecked, setIsOrderNumberChecked] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const division = useSelector(
    (state) => state.product.previousSearchValues.division
  );

  const handleOpenSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleAddSKU = useCallback(() => {
    setSkus((prevSkus) => [...prevSkus, { sku: '', quantity: 0 }]);
  }, []);

  const handleRemoveSKU = useCallback(() => {
    setSkus((prevSkus) =>
      prevSkus.length > 1 ? prevSkus.slice(0, -1) : prevSkus
    );
  }, []);

  const handleChangeSKU = useCallback(async (event, index, field) => {
    setSkus((prevSkus) => {
      const newSkus = [...prevSkus];
      newSkus[index][field] = event.target.value.trim();
      return newSkus;
    });
  }, []);

  const handleIncrementSKU = useCallback((index, field) => {
    setSkus((prevSkus) => {
      const newSkus = [...prevSkus];
      newSkus[index][field] = parseInt(newSkus[index][field], 10) + 1;
      return newSkus;
    });
  }, []);

  const handleDecrementSKU = useCallback((index, field) => {
    setSkus((prevSkus) => {
      const newSkus = [...prevSkus];
      const currentValue = parseInt(newSkus[index][field], 10);
      if (currentValue > 0) {
        newSkus[index][field] = currentValue - 1;
      }
      return newSkus;
    });
  }, []);

  const handleSubmitSKU = async (event, index) => {
    if (!/^\d{8}$/.test(event.target.value.trim())) {
      handleOpenSnackbar('The SKU must be an 8-digit number.');
      setSkus((prevSkus) =>
        prevSkus.map((sku, i) => (i === index ? { ...sku, sku: '' } : sku))
      );
      return;
    }

    const skuInfo = await getSkuInfo(event.target.value.trim());
    if (!skuInfo || typeof skuInfo === 'string') {
      handleOpenSnackbar('The SKU entered is not valid.');
      setSkus((prevSkus) =>
        prevSkus.map((sku, i) => (i === index ? { ...sku, sku: '' } : sku))
      );
      return;
    }
    setSkus((prevSkus) =>
      prevSkus.map((sku, i) =>
        i === index
          ? {
              ...sku,
              vendor: skuInfo.pmRec.Vendor,
              description: skuInfo.pmRec.VendorDescription,
              color: skuInfo.pmRec.Color,
              theme: skuInfo.pmRec.Theme,
            }
          : sku
      )
    );
  };

  const handleChangeCategory = (event) => {
    setCategory(event.target.value);
  };

  const handleCheckboxChange = (event) => {
    setOrder('');
    setIsOrderNumberChecked(event.target.checked);
  };

  const handleChangeOrder = (event) => {
    setOrder(event.target.value);
  };

  const handleSubmitChanges = () => {
    if (!category) {
      handleOpenSnackbar('Please select a category.');
      return;
    }

    const filledSkus = skus.filter((sku) => sku.sku !== '');

    if (filledSkus.length === 0) {
      handleOpenSnackbar('At least one unexpected sku must be entered.');
      return;
    }

    const hasZeroQuantity = filledSkus.some((sku) => sku.quantity === 0);
    if (hasZeroQuantity) {
      handleOpenSnackbar(
        'All line items must have a quantity greater than zero.'
      );
      return;
    }

    submitChanges(category, filledSkus, order);
  };

  const getSkuInfo = async (sku) => {
    return await OrderServices.getSkuInfo(division, sku);
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle component="div">
          <Typography variant="h3" sx={{ marginY: 1 }}>
            Unexpected Item
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center" sx={{ mb: 1, gap: 1 }}>
            <Typography>Category:</Typography>
            <Select
              required
              value={category}
              onChange={handleChangeCategory}
              sx={{ width: '80%', height: '40px' }}
            >
              <MenuItem value="ADDON">Add-On</MenuItem>
              <MenuItem value="CROSSLOAD">Crossload</MenuItem>
              <MenuItem value="OVERLOAD">Overload</MenuItem>
            </Select>
          </Box>
          <Divider sx={{ mb: 1, borderColor: 'black' }} />
          <Box display="flex" alignItems="center" flexDirection="column">
            <Box display="flex" alignItems="center">
              <Typography>Add Skus: </Typography>
              <IconButton onClick={handleAddSKU}>
                <AddIcon />
              </IconButton>
              <IconButton onClick={handleRemoveSKU}>
                <RemoveIcon />
              </IconButton>
              <Divider orientation="vertical" flexItem sx={{ ml: 1 }} />
              <Box display="flex" alignItems="center" sx={{ ml: 2, gap: 1 }}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Checkbox
                    checked={isOrderNumberChecked}
                    onChange={handleCheckboxChange}
                  />
                  <Typography variant="body2" sx={{ mt: -1 }}>
                    Order #?
                  </Typography>
                </Box>
                <TextField
                  disabled={!isOrderNumberChecked}
                  value={order}
                  onChange={(event) => handleChangeOrder(event)}
                  sx={{
                    '& .MuiInputBase-input': { height: '12px' },
                    backgroundColor: isOrderNumberChecked ? 'white' : '#ddd',
                  }}
                />
              </Box>
            </Box>
            <Divider flexItem sx={{ mt: 1, borderColor: 'black' }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ width: '42.857%' }}>
                      SKU
                    </TableCell>
                    <TableCell align="center" sx={{ width: '42.857%' }}>
                      Description
                    </TableCell>
                    <TableCell align="center" sx={{ width: '14.286%' }}>
                      Quantity
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {skus.map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:not(:last-child)': {
                          borderBottom: '1px solid black',
                        },
                      }}
                    >
                      <TableCell align="center" sx={{ flex: 3 }}>
                        <TextField
                          fullWidth
                          value={item.sku}
                          onChange={(event) =>
                            handleChangeSKU(event, index, 'sku')
                          }
                          onBlur={(event) =>
                            handleSubmitSKU(event, index, 'sku')
                          }
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.target.blur();
                            }
                          }}
                          InputProps={{ style: { height: '45px' } }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ flex: 3 }}>
                        {item.description ? (
                          <Box>
                            <Typography>{item.description}</Typography>
                            <Typography
                              sx={{ fontStyle: 'italic', fontSize: 12 }}
                            >
                              {item.color}/{item.theme}
                            </Typography>
                            <Typography
                              sx={{ fontSize: 13, fontWeight: 'bold' }}
                            >
                              {item.vendor}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography sx={{ color: 'gray' }}>
                            Enter a sku to see details
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ flex: 1 }}>
                        <QuantitySelector
                          increment={() =>
                            handleIncrementSKU(index, 'quantity')
                          }
                          decrement={() =>
                            handleDecrementSKU(index, 'quantity')
                          }
                        >
                          {item.quantity}
                        </QuantitySelector>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          <Button onClick={handleSubmitChanges}>Submit</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
